import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';

interface RedemptionData {
  points: number;
  amount: number;
}

interface RedemptionModalProps {
  show: boolean;
  onHide: () => void;
  currentRedemption: RedemptionData | null;
  onRedemptionSubmit: (redemption: RedemptionData) => void;
}

interface FormErrors {
  points?: string;
  amount?: string;
}

interface FormData {
  points: string;
  amount: string;
}

export const RedemptionModal: React.FC<RedemptionModalProps> = ({
  show,
  onHide,
  currentRedemption,
  onRedemptionSubmit
}) => {
  const [formData, setFormData] = useState<FormData>({
    points: '',
    amount: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Populate form with existing data when modal opens
  useEffect(() => {
    if (show && currentRedemption) {
      setFormData({
        points: currentRedemption.points.toString(),
        amount: currentRedemption.amount.toString()
      });
    } else if (show && !currentRedemption) {
      setFormData({
        points: '',
        amount: ''
      });
    }
    setErrors({});
  }, [show, currentRedemption]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.points.trim()) {
      newErrors.points = 'Points are required';
    } else if (isNaN(Number(formData.points)) || Number(formData.points) <= 0) {
      newErrors.points = 'Points must be a positive number';
    }

    if (!formData.amount.trim()) {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const redemptionData: RedemptionData = {
      points: Number(formData.points),
      amount: Number(formData.amount)
    };

    onRedemptionSubmit(redemptionData);
    handleClose();
  };

  const handleRemove = () => {
    onRedemptionSubmit(null as any); // Remove redemption
    handleClose();
  };

  const handleClose = () => {
    setFormData({ points: '', amount: '' });
    setErrors({});
    onHide();
  };

  const updateField = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="text-secondary fw-normal">Redemption</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {currentRedemption && (
          <div className="alert alert-info mb-3">
            <i className="bx bx-info-circle me-2"></i>
            Current redemption: <strong>{currentRedemption.points} points = ₹{currentRedemption.amount}</strong>
          </div>
        )}
        
        <Form>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="text-secondary">
                  Points <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter points"
                  value={formData.points}
                  onChange={(e) => updateField('points', e.target.value)}
                  isInvalid={!!errors.points}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.points}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="text-secondary">
                  Amount <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  placeholder="Enter amount"
                  value={formData.amount}
                  onChange={(e) => updateField('amount', e.target.value)}
                  isInvalid={!!errors.amount}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.amount}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        {currentRedemption && (
          <Button variant="warning" onClick={handleRemove}>
            Remove
          </Button>
        )}
        <Button variant="primary" onClick={handleSubmit}>
          {currentRedemption ? 'Update' : 'Submit'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};